import React, { useCallback, useMemo, useState } from 'react';
import {
  AssignGroup,
  AvatarImage,
  ButtonIcon,
  DeadBlock,
  Deadline,
  Form,
  IconDueDate,
  IconHidden,
  Label,
  ProjectName,
  StatusDropdown,
  TaskContent,
  TaskCount,
  TaskItemWrapper,
  Text,
  TooltipContent
} from '../styles';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import { StatusListInterface } from '../../../../interfaces/ProjectInterface';
import Linkify from 'react-linkify';
import { calculateDueTime, isEmpty } from '../../../../helpers/common';
import Avatar from '../../../../component/avatar';
import dayjs from 'dayjs';
import { useMobileDevice } from '../../../../global/useMobile';
import { AVATAR_BORDER, TASK_FILTER_PROPERTY_TYPE, ICON_SIZE } from '../../../../global/constants';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import { useHistory } from 'react-router-dom';
import { TASK_ANALYTICS } from '../../../../global/analyticsConstants';
import Tooltip from '../../../../component/tooltip';
import TaskLabelList from './taskLabelList';
import AssignBoxV2 from '../../../../component/dropdowns/assignPopup';
import {
  useTaskAssignActions,
  useViewFilterAssignActions
} from '../../../../component/dropdowns/assignPopup/assignActions';
import { TaskModel } from '../../../../mobx/models/task';
import { ProjectModel } from '../../../../mobx/models/project';
import { observer } from 'mobx-react-lite';
import MyCustomContextMenuV2 from '../../../../component/customContextMenu';
import PriorityDropdownV2 from '../../../../component/dropdowns/priorityDropdown';
import TaskStatusDropdownV2 from '../../../../component/dropdowns/taskStatusDropdown';
import { rootStore } from '../../../../mobx/rootStore';
import { ProjectTemplateModel } from '../../../../mobx/models/projectTemplate';
import { TaskTitle } from '../../../../component/tasks/styles';
import { TaskId } from '../taskDetail/styles';
import CheckboxInput from '../../../../component/input/checkboxInput';
import { ROUTES } from '../../../../routes/urls';
// Shared stopPropagation handler to deduplicate per-row useCallback
const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

const VIEW_FILTER_CONTEXT = { mode: 'viewFilter' } as const;
// Shared Linkify decorator to avoid creating a new function reference per row render
const linkDecorator = (decoratedHref: string, decoratedText: string, key: number) => (
  <a target='blank' href={decoratedHref} key={key}>
    {decoratedText}
  </a>
);

interface Props {
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
  dragHandleProps?: Record<string, unknown>;
  onClickCheckbox?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClickTaskCheckBox?: (task: TaskModel) => void;
  task: TaskModel;
  currentProject: ProjectModel | ProjectTemplateModel;
  isTemplateProject?: boolean;
  loadData: () => void;
  isSelected?: boolean;
  onUpdateStatus?: (status: StatusListInterface, previousStatus: StatusListInterface) => void;
  onChangeStatus?: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  handleHoverTask?: (groupId: string, taskId: string, isCompleted?: boolean) => void;
  groupId?: string;
  isCompletedTask?: boolean;
  isOtherTask?: boolean;
  setOpen: (value: boolean) => void;
  setDeleteModelopen: (value: boolean) => void;
  setSelectedItem?: (value: TaskModel) => void;
  isSubTask?: boolean;
  handleSubtaskOpen?: (id: string) => void;
  isActiveSubTask?: boolean;
  isView?: boolean;
  showProjectName?: boolean;
  applyDisplayProperties?: boolean;
  maxVisibleLabels: number;
}

const TaskItem = ({
  onMouseEnter: onMouseEnterProp,
  onMouseLeave: onMouseLeaveProp,
  dragHandleProps,
  onClickCheckbox: onClickCheckboxProp,
  onClickTaskCheckBox,
  task,
  currentProject: currentProjectProps,
  isTemplateProject,
  isSelected,
  loadData,
  onUpdateStatus: onUpdateStatusProp,
  onChangeStatus,
  handleHoverTask,
  groupId,
  isOtherTask,
  isCompletedTask,
  setOpen,
  setDeleteModelopen,
  setSelectedItem,
  isSubTask,
  handleSubtaskOpen,
  isActiveSubTask,
  isView,
  showProjectName = false,
  applyDisplayProperties = true,
  maxVisibleLabels
}: Props) => {
  // States
  const [currentActiveTask, setCurrentActiveTask] = useState<string>('');
  //use selector state variables
  const { currentUser, projectStore } = rootStore;
  const taskAssignActions = useTaskAssignActions(task, { isProjectTemplate: isTemplateProject });
  const viewFilterAssignActions = useViewFilterAssignActions();
  const assignActions = isView ? viewFilterAssignActions : taskAssignActions;
  const assignContext = useMemo(
    () => (isView ? VIEW_FILTER_CONTEXT : { mode: 'task' as const, currentTask: task }),
    [isView, task],
  );

  // Memoized display properties object to optimize store access
  type TaskFilterPropertyValues = (typeof TASK_FILTER_PROPERTY_TYPE)[keyof typeof TASK_FILTER_PROPERTY_TYPE];

  const displayProperties = useMemo<Partial<Record<TaskFilterPropertyValues, boolean>>>(() => {
    return projectStore.currentProject?.displayPropertiesFilterWithFirebase ?? {};
  }, [projectStore.currentProject?.displayPropertiesFilterWithFirebase]);

  // Other variables
  const mobile = useMobileDevice();
  const history = useHistory();
  const currentProject = task.project || currentProjectProps;

  // Memoized callback handlers — avoids inline arrow allocations in parent
  const onClickCheckbox = useCallback(
    (_e?: React.ChangeEvent<HTMLInputElement>) => {
      if (onClickTaskCheckBox) {
        onClickTaskCheckBox(task);
      } else if (onClickCheckboxProp) {
        onClickCheckboxProp(_e!);
      }
    },
    [onClickTaskCheckBox, onClickCheckboxProp, task]
  );

  const onUpdateStatus = useCallback(
    (status: StatusListInterface, previousStatus: StatusListInterface) => {
      if (onChangeStatus) {
        onChangeStatus(task, status, previousStatus);
      } else if (onUpdateStatusProp) {
        onUpdateStatusProp(status, previousStatus);
      }
    },
    [onChangeStatus, onUpdateStatusProp, task]
  );

  const handleContextMenuUpdateStatus = useCallback(
    (contextTask: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => {
      if (onChangeStatus) {
        onChangeStatus(contextTask, status, previousStatus);
      } else if (onUpdateStatusProp) {
        onUpdateStatusProp(status, previousStatus);
      }
    },
    [onChangeStatus, onUpdateStatusProp]
  );

  const onMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (onMouseEnterProp) {
        onMouseEnterProp(e);
      } else if (handleHoverTask && groupId) {
        handleHoverTask(groupId, task?.id || '', isCompletedTask);
      }
    },
    [onMouseEnterProp, handleHoverTask, groupId, task?.id, isCompletedTask]
  );

  const onMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (onMouseLeaveProp) {
        onMouseLeaveProp(e);
      } else if (handleHoverTask) {
        handleHoverTask('', '', isCompletedTask);
      }
    },
    [onMouseLeaveProp, handleHoverTask, isCompletedTask]
  );

  // Memoized expensive calculations
  const dueResult = useMemo(() => {
    return task?.dueDate ? calculateDueTime(task.dueDate) : undefined;
  }, [task?.dueDate]);

  const dueDate = useMemo(() => {
    return task?.dueDate?.toISOString();
  }, [task?.dueDate]);

  const statusDetails = useMemo(() => {
    return currentProject?.defaultStatus || currentProject?.statusData?.find((x) => x['_id'] === task?.statusId);
  }, [currentProject?.defaultStatus, currentProject?.statusData, task?.statusId]);

  const onClickTaskDetails = useCallback(
    (task: TaskModel) => {
      if (isTemplateProject) return;
      const eventProperties = {
        source: 'task list',
        title: task?.name || ''
      };
      trackAnalyticActivity(TASK_ANALYTICS.VIEW, eventProperties);
      rootStore.taskStore.setCurrentTask(task?.id);
      history.push(ROUTES.TASK_DETAILS.replace(':id', task?.projectId || '').replace(':taskId', task?.id || ''));
    },
    [isTemplateProject, history]
  );

  const onClickClone = useCallback(
    async (task: TaskModel) => {
      if (isTemplateProject) {
        rootStore.taskStore.createTaskInput.updateWith(task);
        rootStore.taskStore.createTaskInput.setProject(currentProject?.id, true);
        rootStore.taskStore.createTaskInput.setId(undefined);
      } else {
        rootStore.taskStore.createTaskInput.updateWith(task);
        rootStore.taskStore.createTaskInput.setDueDate(null);
        rootStore.taskStore.createTaskInput.setId(undefined);
      }

      // Set cloneTaskId if the task has subtasks
      if (task.subTasks && task.subTasks.length > 0) {
        rootStore.taskStore.createTaskInput.setCloneTaskId(task.id);
      }

      setOpen(true);
    },
    [isTemplateProject, currentProject, setOpen]
  );

  const onClickEdit = useCallback(
    async (task: TaskModel) => {
      if (isTemplateProject) {
        if (currentProject) {
          rootStore.taskStore.createTaskInput.setId(task?.id);
          rootStore.taskStore.createTaskInput.updateWith(task);
          rootStore.taskStore.createTaskInput.setProject(currentProject?.id, true);
        }
      } else {
        rootStore.taskStore.createTaskInput.updateWith(task);
      }
      setOpen(true);
    },
    [isTemplateProject, currentProject, setOpen]
  );

  const onClickProject = useCallback(
    (projectId: string, e: React.SyntheticEvent) => {
      e.stopPropagation();
      history.push(ROUTES.PROJECT_TASKS.replace(':id', projectId));
    },
    [history]
  );

  const openDeleteModel = useCallback(
    (task?: TaskModel) => {
      if (task && setSelectedItem) setSelectedItem(task);
      setDeleteModelopen(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setDeleteModelopen is useState setter, always stable, doesn't need to be in deps
    [setSelectedItem]
  );

  const handleClickView = useCallback(() => onClickTaskDetails(task), [onClickTaskDetails, task]);
  const handleClickClone = useCallback(() => onClickClone(task), [onClickClone, task]);
  const handleClickEdit = useCallback(() => onClickEdit(task), [onClickEdit, task]);
  const handleClickDelete = useCallback(() => openDeleteModel(task), [openDeleteModel, task]);

  const handleSubtaskClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleSubtaskOpen?.(task?.id);
    },
    [handleSubtaskOpen, task?.id]
  );

  const handleSetCurrentActiveTask = useCallback((taskId: string) => {
    setCurrentActiveTask(taskId);
  }, []);

  const handleAssignClick = useCallback(() => {
    if (mobile) rootStore.taskStore.setCurrentAssignBoxTaskId(task?.id);
  }, [mobile, task?.id]);

  //render task title name
  const renderTaskTitle = useCallback(
    (task: TaskModel) => {
      const shouldShowId = displayProperties[TASK_FILTER_PROPERTY_TYPE.ID] !== false || !applyDisplayProperties;

      return (
        <TaskTitle>
          {!isEmpty(task?.project?.identifier) && !isView && !isTemplateProject && shouldShowId && (
            <TaskId>
              {task?.project?.identifier}-{task?.taskNumber}
            </TaskId>
          )}
          <Label>{task?.name}</Label>
        </TaskTitle>
      );
    },
    [isView, isTemplateProject, applyDisplayProperties, displayProperties]
  );

  const labelData = useMemo(() => {
    if (!currentProject?.labelsEnabled || !task?.labelsIds) return null;
    return isTemplateProject
      ? currentProject?.labelsList?.filter((label) => task.labelsIds.includes(label?.id))
      : task?.labels;
  }, [currentProject?.labelsEnabled, currentProject?.labelsList, isTemplateProject, task?.labelsIds, task?.labels]);

  const labelsVisible = useMemo(() => {
    return isView || displayProperties[TASK_FILTER_PROPERTY_TYPE.LABELS] !== false;
  }, [displayProperties, isView]);

  return (
    <>
      <MyCustomContextMenuV2
        taskData={task}
        loadData={loadData}
        currentProject={currentProject}
        setCurrentActiveTask={handleSetCurrentActiveTask}
        onClickView={handleClickView}
        onClickClone={handleClickClone}
        onClickEdit={handleClickEdit}
        onClickDelete={handleClickDelete}
        statusList={currentProject?.statusData || []}
        onUpdateStatus={handleContextMenuUpdateStatus}
        isSubTask={!!task?.parentTaskId}
        isTemplateProject={isTemplateProject}
        isView={isView}>
        <TaskContent
          role='button'
          onClick={() => onClickTaskDetails(task)}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          isSelected={isSelected}
          isActive={task?.id === currentActiveTask}
          id={`${task?.id}`}
          className={isSelected ? 'selected-task' : ''}
          showSubTask={isSubTask}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClickTaskDetails(task);
            }
          }}>
          <IconHidden
            role='button'
            onClick={stopPropagation}
            {...(!isOtherTask && dragHandleProps ? dragHandleProps : {})}
            showSubTask={isSubTask}>
            <SVGIcon
              name='new-three-dots-vertical-icon'
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-secondary)'
            />
          </IconHidden>
          <TaskItemWrapper>
            <Form isTaskItem={true}>
              <StatusDropdown>
                {(isView ||
                  !applyDisplayProperties ||
                  displayProperties[TASK_FILTER_PROPERTY_TYPE.STATUS] !== false) && (
                  <>
                    {statusDetails && currentProject?.statusEnable ? (
                      <TaskStatusDropdownV2
                        defaultStatus={currentProject?.defaultStatus}
                        statusList={currentProject?.statusData || []}
                        currentStatusId={task?.statusId}
                        isTaskCheckBox={true}
                        onUpdateStatus={onUpdateStatus}
                        isTemplateProject={isTemplateProject}
                        isIconButton={true}
                      />
                    ) : (
                      <ButtonIcon>
                        <CheckboxInput
                          onClick={stopPropagation}
                          onChange={onClickCheckbox}
                          checked={task?.status}
                          checkboxType='task-checkbox'
                        />
                      </ButtonIcon>
                    )}
                  </>
                )}
                {currentProject?.priority?.enabled &&
                  (isView ||
                    !applyDisplayProperties ||
                    displayProperties[TASK_FILTER_PROPERTY_TYPE.PRIORITY] !== false) && (
                    <PriorityDropdownV2
                      taskData={task}
                      isTaskCheckBox={true}
                      currentProject={currentProject}
                      isTemplateProject={isTemplateProject}
                      isIconButton={true}
                    />
                  )}
              </StatusDropdown>
              <Linkify componentDecorator={linkDecorator}>{renderTaskTitle(task)}</Linkify>
              {task?.subTasks && task?.subTasks?.length > 0 && (
                <TaskCount role='button' isActiveSubTask={isActiveSubTask} onClick={handleSubtaskClick}>
                  {task?.subTasks?.filter((x) => x?.status)?.length}/{task?.subTasks?.length}
                </TaskCount>
              )}
              {/* End of Form content */}
            </Form>
            {(isView || !applyDisplayProperties || displayProperties[TASK_FILTER_PROPERTY_TYPE.ASSIGNEE] !== false) && (
              <AssignGroup role='button' onClick={stopPropagation} className={'assign'}>
                <ul role='button' onClick={handleAssignClick}>
                  {task?.users
                    ?.filter((user) => !!user?.id)
                    ?.map((user) => (
                      <li key={user?.id}>
                        <AvatarImage>
                          <Avatar
                            imgSrc={user?.profile_photo || ''}
                            name={user?.name ? user.name : 'U N'}
                            size={20}
                            borderVariant={AVATAR_BORDER.THIN}
                          />
                        </AvatarImage>
                      </li>
                    ))}
                  <AssignBoxV2
                    projectUsers={currentProject?.users || []}
                    context={assignContext}
                    onAssign={assignActions.onAssign}
                    onUnassign={assignActions.onUnassign}
                    isMultiAssignee={currentProject?.multiAssignee}
                  />
                </ul>
              </AssignGroup>
            )}
          </TaskItemWrapper>
          <Deadline>
            <TaskLabelList
              labelData={labelData}
              labelsVisible={labelsVisible}
              maxVisibleLabels={maxVisibleLabels}
              taskLabels={task?.labels || []}
            />

            {showProjectName && (
              <ProjectName
                role='button'
                showRightBorder={!!task?.dueDate}
                onClick={(e) => onClickProject(task?.projectId, e)}>
                <p>{task?.project?.name}</p>
              </ProjectName>
            )}
            {!isTemplateProject &&
              task?.dueDate &&
              (isView ||
                !applyDisplayProperties ||
                displayProperties[TASK_FILTER_PROPERTY_TYPE.DUE_DATE] !== false) && (
                <DeadBlock>
                  <Tooltip
                    title={
                      <>
                        <div>Due {dueResult?.dueParam}</div>
                        <div>Due on {dayjs(dueDate).format(currentUser?.dateFormat)}</div>
                      </>
                    }
                    placement='bottom'
                    maxWidth={'max-content'}>
                    <TooltipContent>
                      <Text isOverdue={dueResult?.overDue && !task?.status}>{task.dueDate && dueResult?.dueTime}</Text>

                      <IconDueDate role='button' onClick={stopPropagation}>
                        {dueResult?.overDue && !task?.status ? (
                          <SVGIcon
                            name='tasklist-calendar-duedate-icon'
                            width={ICON_SIZE.XXL}
                            height={ICON_SIZE.XXL}
                            viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                            className='calendar-due-fill-color'
                          />
                        ) : (
                          <SVGIcon
                            name='tasklist-calendar-icon'
                            width={ICON_SIZE.XXL}
                            height={ICON_SIZE.XXL}
                            viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                            className='calendar-fill-color'
                          />
                        )}
                      </IconDueDate>
                    </TooltipContent>
                  </Tooltip>
                </DeadBlock>
              )}
          </Deadline>
        </TaskContent>
      </MyCustomContextMenuV2>
    </>
  );
};

const areEqual = (prev: Props, next: Props) => {
  if (prev.task?.id !== next.task?.id) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.currentProject?.id !== next.currentProject?.id) return false;
  if (prev.isTemplateProject !== next.isTemplateProject) return false;
  if (prev.isView !== next.isView) return false;
  if (prev.isActiveSubTask !== next.isActiveSubTask) return false;
  if (prev.maxVisibleLabels !== next.maxVisibleLabels) return false;
  if (prev.showProjectName !== next.showProjectName) return false;
  if (prev.applyDisplayProperties !== next.applyDisplayProperties) return false;
  if (prev.isSubTask !== next.isSubTask) return false;
  return true;
};

export default React.memo(observer(TaskItem), areEqual);
